/**
 * Query Keys centralisés pour React Query
 * Garantit la cohérence des clés de cache et facilite l'invalidation
 *
 * Certaines clés gardent une valeur historique hors du préfixe de leur domaine
 * (signalées « valeur historique ») : la ranger sous le préfixe changerait les
 * requêtes rafraîchies par les invalidations existantes.
 */

export const queryKeys = {
  /**
   * Clés pour les projets
   */
  projects: {
    all: ['projects'] as const,
    lists: () => [...queryKeys.projects.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.projects.lists(), filters] as const,
    details: () => [...queryKeys.projects.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.projects.details(), id] as const,
    /** Liste minimale (id + nom) pour les sélecteurs */
    options: () => [...queryKeys.projects.all, 'options'] as const,
    /** Liste du calendrier éditorial (id, nom, description) */
    calendarOptions: () => [...queryKeys.projects.options(), 'calendar'] as const,
    /** Page Projets : projets visibles selon le type d'accès de l'utilisateur */
    overview: (
      profileId: string | undefined,
      access: { all: boolean; assigned: boolean; own: boolean }
    ) => [...queryKeys.projects.all, 'overview', profileId, access] as const,
    /** Résumés (id, nom, budget) de tous les projets */
    summaries: () => [...queryKeys.projects.all, 'summaries'] as const,
    /** Résumés des projets accessibles à un chef de projet */
    chefSummaries: (userId: string | undefined) =>
      [...queryKeys.projects.summaries(), 'chef', userId] as const,
    /** Projets d'une entreprise (dashboard client) */
    byCompany: (companyId: string | undefined) =>
      [...queryKeys.projects.all, 'company', companyId] as const,
    /** Sélecteur de projet du board Kanban (valeur historique) */
    taskBoardOptions: (userId: string | undefined) => ['user-projects', userId] as const,
  },

  /**
   * Clés pour les membres explicites d'un projet (table project_members).
   * Deux formes de données distinctes : invalider `byProject` rafraîchit les deux.
   */
  projectMembers: {
    all: ['project-members'] as const,
    byProject: (projectId: string) => [...queryKeys.projectMembers.all, projectId] as const,
    /** Membres avec profil joint (onglet utilisateurs, gestion par activation) */
    list: (projectId: string) => [...queryKeys.projectMembers.byProject(projectId), 'list'] as const,
    /** Personnes assignables aux paiements : membres + admins/chefs implicites, noms aplatis */
    assignable: (projectId: string) =>
      [...queryKeys.projectMembers.byProject(projectId), 'assignable'] as const,
  },

  /**
   * Clés pour les budgets - Lignes de dépenses
   */
  budgetExpenseLines: {
    all: ['budget_expense_lines'] as const,
    lists: () => [...queryKeys.budgetExpenseLines.all, 'list'] as const,
    list: (projectId: string) => [...queryKeys.budgetExpenseLines.lists(), projectId] as const,
    details: () => [...queryKeys.budgetExpenseLines.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.budgetExpenseLines.details(), id] as const,
  },

  /**
   * Clés pour le prévu des utilisations de la marge (sous-catégories de la catégorie Marge)
   */
  marginAllocations: {
    all: ['budget_margin_allocations'] as const,
    list: (projectId: string) => [...queryKeys.marginAllocations.all, 'list', projectId] as const,
    /** Vue globale des marges (tous les projets) */
    overview: () => [...queryKeys.marginAllocations.all, 'overview'] as const,
  },

  /**
   * Clés pour les budgets - Lignes prestataires
   */
  budgetProviderLines: {
    all: ['budget_provider_lines'] as const,
    lists: () => [...queryKeys.budgetProviderLines.all, 'list'] as const,
    list: (projectId: string) => [...queryKeys.budgetProviderLines.lists(), projectId] as const,
    details: () => [...queryKeys.budgetProviderLines.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.budgetProviderLines.details(), id] as const,
  },

  /**
   * Clés pour les paiements
   */
  payments: {
    all: ['payments'] as const,
    lists: () => [...queryKeys.payments.all, 'list'] as const,
    list: (projectId: string) => [...queryKeys.payments.lists(), projectId] as const,
    byProject: (projectId: string) => [...queryKeys.payments.lists(), 'project', projectId] as const,
    details: () => [...queryKeys.payments.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.payments.details(), id] as const,
  },

  /**
   * Clés pour les activations
   */
  activations: {
    all: ['activations'] as const,
    lists: () => [...queryKeys.activations.all, 'list'] as const,
    list: (projectId: string) => [...queryKeys.activations.lists(), projectId] as const,
    details: () => [...queryKeys.activations.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.activations.details(), id] as const,
  },

  /**
   * Clés pour les types de dépenses
   */
  expenseTypes: {
    all: ['expense_types'] as const,
    lists: () => [...queryKeys.expenseTypes.all, 'list'] as const,
    list: (projectId: string) => [...queryKeys.expenseTypes.lists(), projectId] as const,
    details: () => [...queryKeys.expenseTypes.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.expenseTypes.details(), id] as const,
    /** Types hors Marge (id + nom) pour les sélecteurs */
    options: (projectId: string) => [...queryKeys.expenseTypes.all, 'options', projectId] as const,
  },

  /**
   * Clés pour les sous-catégories de dépenses
   */
  expenseSubcategories: {
    all: ['expense_subcategories'] as const,
    lists: () => [...queryKeys.expenseSubcategories.all, 'list'] as const,
    list: (expenseTypeId: string) => [...queryKeys.expenseSubcategories.lists(), expenseTypeId] as const,
    details: () => [...queryKeys.expenseSubcategories.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.expenseSubcategories.details(), id] as const,
    /** Sous-catégories d'un type (id, nom, type) pour les sélecteurs */
    options: (expenseTypeId: string) =>
      [...queryKeys.expenseSubcategories.all, 'options', expenseTypeId] as const,
  },

  /**
   * Clés pour les invitations
   */
  invitations: {
    all: ['invitations'] as const,
    lists: () => [...queryKeys.invitations.all, 'list'] as const,
    validate: (token: string | null) => [...queryKeys.invitations.all, 'validate', token] as const,
    details: () => [...queryKeys.invitations.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.invitations.details(), id] as const,
  },

  /**
   * Clés pour les tâches
   */
  tasks: {
    all: ['tasks'] as const,
    lists: () => [...queryKeys.tasks.all, 'list'] as const,
    list: (projectId: string) => [...queryKeys.tasks.lists(), projectId] as const,
    details: () => [...queryKeys.tasks.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.tasks.details(), id] as const,
    /** Tâches de l'utilisateur sur un projet, board Kanban (valeur historique) */
    board: (userId: string | undefined, projectId: string) =>
      ['my-tasks', userId, projectId] as const,
  },

  /**
   * Clés pour les assignations chef de projet <-> activations
   */
  projectManagerActivations: {
    all: ['project_manager_activations'] as const,
    lists: () => [...queryKeys.projectManagerActivations.all, 'list'] as const,
    byProject: (projectId: string) => [...queryKeys.projectManagerActivations.all, projectId] as const,
    byUser: (userId: string) => [...queryKeys.projectManagerActivations.all, 'user', userId] as const,
    accessibleActivations: (projectId: string, userId: string) =>
      ['accessible_activations', projectId, userId] as const,
  },

  /**
   * Clés pour les factures
   */
  invoices: {
    all: ['invoices'] as const,
    lists: () => [...queryKeys.invoices.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.invoices.lists(), filters] as const,
    byProvider: (providerId: string) => [...queryKeys.invoices.lists(), 'provider', providerId] as const,
    byProject: (projectId: string) => [...queryKeys.invoices.lists(), 'project', projectId] as const,
    /** Factures brutes du dashboard prestataire (valeur historique) */
    providerDashboard: (providerId: string | undefined) =>
      ['prestataire-invoices', providerId] as const,
    details: () => [...queryKeys.invoices.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.invoices.details(), id] as const,
  },

  /**
   * Clés pour les utilisateurs
   */
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.users.details(), id] as const,
    current: () => [...queryKeys.users.all, 'current'] as const,
    /** Profils actifs des rôles donnés */
    activeByRoles: (roles: readonly string[]) =>
      [...queryKeys.users.all, 'active-by-roles', roles] as const,
    /** Personnes assignables (admin, chef de projet, prestataire actifs) */
    assignable: () => [...queryKeys.users.all, 'assignable'] as const,
    /** Identité affichée dans l'en-tête (valeur historique) */
    headerIdentity: (userId: string | undefined) => ['user-profile', userId] as const,
    /** Identité Google liée à un utilisateur (valeur historique) */
    hasGoogleIdentity: (userId: string | undefined) => ['has-google-identity', userId] as const,
    /** Prestataires enregistrés, pour la fusion d'un externe (valeur historique) */
    registeredProvidersForMerge: () => ['registered-providers-for-merge'] as const,
  },

  /**
   * Clés pour les métriques
   */
  metrics: {
    all: ['metrics'] as const,
    project: (projectId: string) => [...queryKeys.metrics.all, 'project', projectId] as const,
    dashboard: () => [...queryKeys.metrics.all, 'dashboard'] as const,
    /** Budget total / engagé des projets d'une entreprise (dashboard client) */
    clientBudget: (companyId: string | undefined, projectIds: string[]) =>
      [...queryKeys.metrics.all, 'client-budget', companyId, projectIds.join(',')] as const,
    /** Budget d'un projet vu par le client (projection sans marge) */
    clientProjectBudget: (projectId: string) =>
      [...queryKeys.metrics.all, 'client-project-budget', projectId] as const,
  },

  /**
   * Clés pour le dashboard admin
   */
  admin: {
    all: ['admin'] as const,
    projects: () => [...queryKeys.admin.all, 'projects'] as const,
    companiesCount: () => [...queryKeys.admin.all, 'companies-count'] as const,
    users: () => [...queryKeys.admin.all, 'users'] as const,
    /** Factures en attente de validation */
    pendingInvoicesCount: () => [...queryKeys.admin.all, 'pendingInvoices'] as const,
    /** Page Entreprises : entreprises avec leurs projets */
    clients: () => [...queryKeys.admin.all, 'clients'] as const,
    /** Page Entreprises : projets et leur entreprise de rattachement */
    projectsForClients: () => [...queryKeys.admin.all, 'projects-for-clients'] as const,
    /** Page Entreprises : utilisateurs clients avec e-mail */
    clientUsers: () => [...queryKeys.admin.all, 'client-users'] as const,
  },

  /**
   * Clés pour les informations prestataires
   */
  providerInfo: {
    all: ['provider-info'] as const,
    detail: (prestataireId: string) => [...queryKeys.providerInfo.all, prestataireId] as const,
  },

  /**
   * Cles pour les prestataires externes
   */
  externalProviders: {
    all: ['external_providers'] as const,
    lists: () => [...queryKeys.externalProviders.all, 'list'] as const,
    unlinked: () => [...queryKeys.externalProviders.all, 'unlinked'] as const,
    /** Prestataires enregistrés + externes (valeur historique) */
    allWithRegistered: () => ['all-providers'] as const,
  },

  /**
   * Clés pour la gamification prestataire
   */
  gamification: {
    all: ['gamification'] as const,
    detail: (userId: string) => ['gamification', userId] as const,
  },

  /**
   * Clés pour le calendrier éditorial
   */
  editorialCalendar: {
    all: ['editorial_calendar'] as const,
    lists: () => [...queryKeys.editorialCalendar.all, 'list'] as const,
    list: (projectId: string, filters?: unknown) =>
      [...queryKeys.editorialCalendar.lists(), projectId, filters] as const,
    details: () => [...queryKeys.editorialCalendar.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.editorialCalendar.details(), id] as const,
    comments: (contentId: string) =>
      [...queryKeys.editorialCalendar.all, 'comments', contentId] as const,
    /** Entrées affichées dans le Kanban des tâches (valeur historique) */
    kanban: (projectId: string) => ['calendar-entries-kanban', projectId] as const,
  },

  /**
   * Clés pour les typologies de contenu
   */
  contentTypologies: {
    all: ['content_typologies'] as const,
    lists: () => [...queryKeys.contentTypologies.all, 'list'] as const,
    list: (projectId: string) => [...queryKeys.contentTypologies.lists(), projectId] as const,
    details: () => [...queryKeys.contentTypologies.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.contentTypologies.details(), id] as const,
  },

  /**
   * Clés pour les entreprises
   */
  companies: {
    all: ['companies'] as const,
    lists: () => [...queryKeys.companies.all, 'list'] as const,
    options: () => [...queryKeys.companies.all, 'options'] as const,
    details: () => [...queryKeys.companies.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.companies.details(), id] as const,
    publicPage: (companyId: string) =>
      [...queryKeys.companies.all, 'public-page', companyId] as const,
  },
} as const;

/**
 * Type helper pour extraire le type d'une clé
 */
export type QueryKey = typeof queryKeys[keyof typeof queryKeys];
